import {
    Component,
    Prop,
    Element,
    State,
    ComponentInterface,
    Event,
    EventEmitter,
    Watch,
    h,
    Host,
    Listen, Method
} from '@stencil/core';

import { WcsTabsAlignment, WcsTabChangeEvent } from './tabs-interface';
import { AriaAttributeName, MutableAriaAttribute } from "../../utils/mutable-aria-attribute";
import {
    inheritAriaAttributes,
    inheritAttributes,
    setOrRemoveAttribute
} from "../../utils/helpers";
import { SelectArrow } from "../select/select-arrow";
import { createPopper, Instance } from "@popperjs/core";

const TABS_INHERITED_ATTRS = [];

/**
 * Tabs component to switch between tab content. Use in conjunction with `wcs-tab`.
 * 
 * ## Accessibility guidelines 💡
 * > - Mobile display should be used for narrower screens (automatically set by default).
 * > - The component respects the W3C [tab pattern](https://www.w3.org/WAI/ARIA/apg/patterns/tabs/)
 * 
 * @cssprop --wcs-tabs-indicator-height - Height of the tabs indicator 
 * @cssprop --wcs-tabs-indicator-background-color - Background color of the tabs indicator
 * @cssprop --wcs-tabs-indicator-border-radius - Border radius of the tabs indicator
 * @cssprop --wcs-tabs-color-default - Text color of the tabs
 * @cssprop --wcs-tabs-color-hover - Text color of the tabs when hovered
 * @cssprop --wcs-tabs-color-press - Text color of the tabs when pressed
 * @cssprop --wcs-tabs-color-focus - Text color of the tabs when focused
 * @cssprop --wcs-tabs-color-selected - Text color of the tabs when selected
 * @cssprop --wcs-tabs-background-color-focus - Background color of the tabs when focused
 * @cssprop --wcs-tabs-background-color-hover - Background color of the tabs when hovered
 * @cssprop --wcs-tabs-background-color-press - Background color of the tabs when pressed
 * @cssprop --wcs-tabs-border-radius - Border radius of the tabs
 * @cssprop --wcs-tabs-border-color-focus - Outline color on a focused tab
 * @cssprop --wcs-tabs-font-weight-default - Font weight of the tabs
 * @cssprop --wcs-tabs-font-weight-selected - Font weight of the tabs when selected
 * @cssprop --wcs-tabs-padding-top - Padding top of the tabs
 * @cssprop --wcs-tabs-padding-right - Padding right of the tabs
 * @cssprop --wcs-tabs-padding-bottom - Padding bottom of the tabs
 * @cssprop --wcs-tabs-padding-left - Padding left of the tabs
 * @cssprop --wcs-tabs-headers-border-bottom - Border bottom (gutter) below the tabs
 * @cssprop --wcs-tabs-transition-duration - Transition duration of the tabs
 * @cssprop --wcs-tabs-mobile-breakpoint - Breakpoint for mobile display (default: 575px)
 * @cssprop --wcs-tabs-mobile-overlay-border-width - Border width of the mobile overlay
 * @cssprop --wcs-tabs-mobile-overlay-border-color - Border color of the mobile overlay
 * @cssprop --wcs-tabs-mobile-overlay-background-color - Background color of the mobile overlay
 * @cssprop --wcs-tabs-mobile-overlay-padding - Padding of the mobile overlay
 * @cssprop --wcs-tabs-mobile-overlay-border-radius - Border radius of the mobile overlay
 * @cssprop --wcs-tabs-mobile-gap - Gap between the mobile tabs in the overlay
 * @cssprop --wcs-tabs-mobile-padding - Padding of the mobile tabs in the overlay
 * @cssprop --wcs-tabs-mobile-height - Height of the mobile tabs in the overlay
 * @cssprop --wcs-tabs-mobile-font-weight-default - Default weight of the mobile tabs in the overlay
 * @cssprop --wcs-tabs-mobile-font-weight-active - Active font weight of the mobile tabs in the overlay
 * @cssprop --wcs-tabs-mobile-font-size - Font size of the mobile tabs in the overlay
 * @cssprop --wcs-tabs-mobile-color - Text color of the mobile tabs in the overlay
 * @cssprop --wcs-tabs-mobile-background-color-default - Default background color of the mobile tabs in the overlay
 * @cssprop --wcs-tabs-mobile-background-color-focus - Focused background color of the mobile tabs in the overlay
 * @cssprop --wcs-tabs-mobile-background-color-hover - Hovered background color of the mobile tabs in the overlay
 * @cssprop --wcs-tabs-mobile-background-color-press - Pressed background color of the mobile tabs in the overlay
 * @cssprop --wcs-tabs-mobile-border-radius - Border radius of the mobile tabs in the overlay
 */
@Component({
    tag: 'wcs-tabs',
    styleUrl: 'tabs.scss',
    shadow: true,
})
export class Tabs implements ComponentInterface, MutableAriaAttribute {
    @Element() private el!: HTMLWcsTabsElement;
    private nativeTablist!: HTMLElement;
    private inheritedAttributes: { [k: string]: any } = {};
    
    /**
     * Tab headers alignment.
     */
    @Prop({reflect: true}) align: WcsTabsAlignment = 'start';

    /**
     * Current selected tab index.
     * Starts at 0.
     */
    @Prop() selectedIndex: number = 0;

    @Prop() selectedKey: any;

    /**
     * Whether to skip rendering the tabpanel with the content of the selected tab. Use this prop if you plan to
     * separately render the tab content.
     */
    @Prop({ reflect: true }) headersOnly: boolean = false;
    /** Determines if tabs header should have a border at the bottom */
    @Prop({ reflect: true }) gutter: boolean;

    /**
     * Description is used to provide aria-label for the tabs container which has `role="tablist"`.
     */
    @Prop() description: string;

    // region MOBILE
    
    /**
     * Mobile display : This div is shown on the user interface when the tabs are expanded
     * @private
     */
    private popoverDiv!: HTMLDivElement;

    private popper: Instance;
    
    @State() private mobileOverlayExpanded: boolean = false;

    /**
     * If true, the tabs will be displayed as a dropdown list containing the tabs. Useful for narrower screens.
     */
    @State() private mobile: boolean = false;

    /**
     * Mobile display: the selected tab semantically become a button that opens an overlay
     */
    private mobileButton!: HTMLButtonElement;

    /**
     * Observe the screen resize to switch between mobile and desktop mode
     */
    private resizeObserver: ResizeObserver;


    // endregion MOBILE

    /**
     *
     * Emitted when the selected tab change.
     */
    @Event() tabChange!: EventEmitter<WcsTabChangeEvent>;

    @State() private headers: string[] = [];

    @State() private currentActiveTabIndex = 0;

    private tabsId: number = tabsId++;

    @Watch('selectedIndex')
    selectedIndexChanged(newValue: number) {
        this.currentActiveTabIndex = newValue;
    }

    @Watch('selectedKey')
    selectedTabkeyChanged(newValue: any) {
        this.updateCurrentActiveIndexByTabKey(newValue);
    }
    
    @Watch('mobile')
    onMobileChange(newValue: boolean) {
        // Remove the popper instance when switching from mobile to desktop for performance
        if (!newValue) {
            this.popper.destroy();
            this.popper = null;
        }
    }

    private emitActiveTabChange() {
        this.tabChange.emit({
            tabName: this.headers[this.currentActiveTabIndex],
            tabIndex: this.currentActiveTabIndex,
            selectedKey: this.tabs[this.currentActiveTabIndex].itemKey
        });
    }

    private updateCurrentActiveIndexByTabKey(newValue: any) {
        for (let i = 0; i < this.tabs.length; i++) {
            const tab = this.tabs[i];
            if (tab.itemKey === newValue) {
                this.currentActiveTabIndex = i;
            }
        }
    }

    @Listen('tabLoaded')
    onTabLoaded() {
        this.refreshHeaders();
    }

    @Listen('click', {target: 'window'})
    onWindowClickEvent(event: MouseEvent) {
        if (this.mobile) {
            const clickedOnMobileButtonOrOverlay = event.composedPath().some(el => el === this.mobileButton || el === this.popoverDiv);
            if (this.mobileOverlayExpanded && !clickedOnMobileButtonOrOverlay) {
                this.mobileOverlayExpanded = false;
            }
        }
    }

    /**
     * Init resize observer for mobile
     */
    private tabsDidLoadWithResizeObserver(): ResizeObserver {
        const smallBreakpoint = getComputedStyle(this.el).getPropertyValue('--wcs-tabs-mobile-breakpoint') || '575px';
        const smallBreakpointValue = parseInt(smallBreakpoint, 10);

        return new ResizeObserver(entry => {
            const cr = entry[0].contentRect;
            const paddingRight = cr.right - cr.width;
            const paddingLeft = cr.left;
            // Switch to mobile mode if the screen is smaller than the breakpoint
            this.mobile = cr.width <= smallBreakpointValue - (paddingLeft + paddingRight);
        });
    }


    private initMobileOverlay() {
        this.popper = createPopper(this.mobileButton, this.popoverDiv, {
            placement: 'bottom-start',
            strategy: 'fixed',
            modifiers: [
                {
                    name: 'offset',
                    options: {
                        offset: [0, 8]
                    }
                }
            ]
        });
    }

    componentDidLoad() {
        this.putTabsInCorrectDivIfTheyAreNot();
        this.refreshHeaders();
        if (this.selectedIndex) {
            this.currentActiveTabIndex = this.selectedIndex;
        }
        if (this.selectedKey) {
            this.updateCurrentActiveIndexByTabKey(this.selectedKey);
        }
        if (!this.resizeObserver) {
            this.resizeObserver = this.tabsDidLoadWithResizeObserver();
            this.resizeObserver.observe(document.body);
        }
    }

    componentDidRender() {
        if (this.mobile) {
            if(!this.popper) {
                this.initMobileOverlay();
            } else {
                this.popper.update();
            }
        }
    }

    // XXX: Firefox < 63
    private putTabsInCorrectDivIfTheyAreNot() {
        const tabDiv = this.el.shadowRoot.querySelector('.wcs-tabs');
        if (tabDiv.querySelector('slot') === null) {
            Array.from(this.el.querySelectorAll('wcs-tab'))
                .filter(node => node.parentNode !== tabDiv)
                .forEach(tab => {
                    if (tab.parentElement.isEqualNode(this.el)) {
                        this.el.removeChild(tab);
                        tabDiv.appendChild(tab);
                    }
                });
        }
    }

    handleKeyDown(ev: KeyboardEvent, tabIndex: number) {
        const target = ev.target as HTMLDivElement;
        switch (ev.key) {
            case ' ':
            case 'Enter': {
                this.selectTabAndEmitChangeEvent(tabIndex);
                ev.preventDefault();
                break;
            }
            case 'ArrowLeft': {
                if (target.previousElementSibling?.classList.contains('wcs-tab-header')) {
                    (target.previousElementSibling as HTMLDivElement).focus();
                    ev.preventDefault();
                }
                break;
            }
            case 'ArrowRight': {
                if (target.nextElementSibling?.classList.contains('wcs-tab-header')) {
                    (target.nextElementSibling as HTMLDivElement).focus();
                    ev.preventDefault();
                }
                break;
            }
            case 'Home': {
                const firstTab = this.el.shadowRoot.querySelector('.wcs-tab-header:first-child');
                if (firstTab) {
                    (firstTab as HTMLDivElement).focus();
                    ev.preventDefault();
                }
                break;
            }
            case 'End': {
                const lastTab = this.el.shadowRoot.querySelector('.wcs-tab-header:last-child');
                if (lastTab) {
                    (lastTab as HTMLDivElement).focus();
                    ev.preventDefault();
                }
                break;
            }
        }
    }

    handleKeyDownMobile(ev: KeyboardEvent, tabIndex: number) {
        const target = ev.target as HTMLDivElement;
        switch (ev.key) {
            case ' ':
            case 'Enter': {
                this.selectTabAndEmitChangeEvent(tabIndex);
                ev.preventDefault();
                break;
            }
            case 'ArrowUp': {
                if (target.previousElementSibling?.classList.contains('wcs-tab-header-mobile')) {
                    (target.previousElementSibling as HTMLDivElement).focus();
                    ev.preventDefault();
                }
                break;
            }
            case 'ArrowDown': {
                if (target.nextElementSibling?.classList.contains('wcs-tab-header-mobile')) {
                    (target.nextElementSibling as HTMLDivElement).focus();
                    ev.preventDefault();
                }
                break;
            }
            case 'Home': {
                const firstTab = this.el.shadowRoot.querySelector('.wcs-tab-header-mobile:first-child');
                if (firstTab) {
                    (firstTab as HTMLDivElement).focus();
                    ev.preventDefault();
                }
                break;
            }
            case 'End': {
                const lastTab = this.el.shadowRoot.querySelector('.wcs-tab-header-mobile:last-child');
                if (lastTab) {
                    (lastTab as HTMLDivElement).focus();
                    ev.preventDefault();
                }
                break;
            }
            case 'Escape': {
                this.mobileOverlayExpanded = false;
                ev.preventDefault();
                break;
            }
        }
    }

    private refreshHeaders() {
        this.headers = [];
        this.tabs
            .forEach(x => {
                this.headers.push(x.getAttribute('header'));
            });
    }

    private get tabs() {
        const tabsEl = this.el.shadowRoot.querySelector('.wcs-tabs');
        // FIXME: problem with this selector being too greedy in ff < 63
        const tabs = this.el.shadowRoot.querySelectorAll('.wcs-tabs > wcs-tab');

        return tabs.length !== 0
            ? tabs
            : tabsEl?.querySelector('slot')
                ? tabsEl?.querySelector('slot')?.assignedElements() as unknown as NodeListOf<HTMLWcsTabElement>
                : [];
    }

    private selectTabAndEmitChangeEvent(index: number) {
        this.currentActiveTabIndex = index;
        this.emitActiveTabChange();
        
        if (this.mobile) {
            this.mobileOverlayExpanded = false;
            this.mobileButton?.focus();
            
        }
    }

    componentWillUpdate() {
        if (!this.headersOnly) {
            this.updateTabVisibility();
        } else {
            this.hideAllTabsContent();
        }
    }

    componentWillLoad(): Promise<void> | void {
        this.inheritedAttributes = {
            ...inheritAriaAttributes(this.el),
            ...inheritAttributes(this.el, TABS_INHERITED_ATTRS),
        };
    }

    disconnectedCallback(): void {
        if (this.popper) {
            this.popper.destroy();
            this.popper = null;
        }
        this.resizeObserver?.disconnect();
    }

    @Method()
    async setAriaAttribute(attr: AriaAttributeName, value: string | null | undefined) {
        setOrRemoveAttribute(this.mobile ? this.popoverDiv : this.nativeTablist, attr, value);
    }

    private updateTabVisibility() {
        this.tabs.forEach((el: HTMLWcsTabElement, idx: number) => {
            if (idx !== this.currentActiveTabIndex) {
                el.hidden = true;
            } else {
                el.hidden = false;
            }
        });
    }

    private hideAllTabsContent() {
        this.tabs.forEach((el: HTMLWcsTabElement) => el.hidden = true);
    }
    
    onMobileButtonClick() {
        this.mobileOverlayExpanded = !this.mobileOverlayExpanded;
        const tabElementToFocus = this.popoverDiv.querySelectorAll('[role=tab]')[this.currentActiveTabIndex] as HTMLElement;
        requestAnimationFrame(() => {
            tabElementToFocus?.focus();
        })
    }
    
    onMobileButtonKeyDown(ev: KeyboardEvent) {
        if (ev.key === 'ArrowDown' || ev.key === 'ArrowUp') {
            this.onMobileButtonClick();
        }
    }

    mobileLayout() {
        return [
            <button id="mobile-button"
                    class="wcs-tab-header active"
                    aria-controls="menu"
                    role="button"
                    aria-expanded={this.mobileOverlayExpanded ? 'true' : 'false'}
                    ref={el => this.mobileButton = el}
                    onClick={() => this.onMobileButtonClick()}
                    onKeyDown={(evt) => this.onMobileButtonKeyDown(evt)}
                    onBlur={($event) => $event.stopImmediatePropagation()}>
                <span>
                    {this.headers[this.currentActiveTabIndex]} <SelectArrow up={this.mobileOverlayExpanded}/>
                </span>
            </button>,
            <div class={(this.mobileOverlayExpanded ? 'show ' : '') + 'popover'}
                 role="tablist"
                 id="menu"
                 aria-label={this.description}
                 aria-orientation="vertical"
                 ref={el => this.popoverDiv = el}
                 tabIndex={-1}
                 {...this.inheritedAttributes}>
                {this.headers.map((header, idx) =>
                    <div class={'wcs-tab-header-mobile ' + (this.currentActiveTabIndex === idx ? 'mobile-active' : '')}
                         onClick={() => this.selectTabAndEmitChangeEvent(idx)}
                         onKeyDown={evt => this.handleKeyDownMobile(evt, idx)}
                         tabIndex={this.currentActiveTabIndex === idx ? 0 : -1}
                         role="tab"
                         id={`tabs-id-${this.tabsId}-tab-id-${idx}`}
                        // aria-controls refers to ID of the tab panel related to the header
                         aria-controls={`tabs-id-${this.tabsId}-tab-panel-${idx}`}
                         aria-label={header}
                         aria-selected={this.currentActiveTabIndex === idx ? 'true' : 'false'}
                    >
                        <span>{header}</span>
                    </div>
                )}
            </div>
        ]
    }
    
    desktopLayout() {
        return (
            this.headers.map((header, idx) =>
                <div class={'wcs-tab-header ' + (this.currentActiveTabIndex === idx ? 'active' : '')}
                     onClick={() => this.selectTabAndEmitChangeEvent(idx)}
                     onKeyDown={evt => this.handleKeyDown(evt, idx)}
                     tabIndex={this.currentActiveTabIndex === idx ? 0 : -1}
                     role="tab"
                     id={`tabs-id-${this.tabsId}-tab-id-${idx}`}
                    // aria-controls refers to ID of the tab panel related to the header
                     aria-controls={`tabs-id-${this.tabsId}-tab-panel-${idx}`}
                     aria-label={header}
                     aria-selected={this.currentActiveTabIndex === idx ? 'true' : 'false'}
                >
                    <span>{header}</span>
                </div>
            )
        )
    }

    render() {
        return (
            <Host>
                <div class="wcs-tabs-headers"
                     role={this.mobile ? null : 'tablist'}
                     ref={(el) => (this.nativeTablist = el)}
                     aria-orientation={this.mobile ? null : 'horizontal'}
                     aria-label={this.mobile ? null : this.description}
                     {...(!this.mobile && this.inheritedAttributes)}>
                    {this.mobile ? this.mobileLayout() : this.desktopLayout()}
                </div>
                <div class="wcs-tabs">
                    <slot onSlotchange={() => this.onTabsSlotChange()} name="wcs-tab"/>
                </div>
            </Host>
        );
    }

    /**
     * Observe when a new tab panel is added to the slot to let's handle accessibility properties for tabs panel:
     * - id: to let header tab refers it proper panel
     * - aria-label: take the same name as it's referenced header name
     * 
     * @private
     */
    private onTabsSlotChange() {
        let tabId = 0;
        this.refreshHeaders();
        this.tabs.forEach(tab => {
            tab.setAttribute("aria-label", this.headers.at(tabId));
            // set an ID to set aria-controls on header tab 
            // (https://www.w3.org/WAI/ARIA/apg/patterns/tabs/examples/tabs-automatic/#:~:text=Refers%20to%20the%20element)
            tab.setAttribute("id", `tabs-id-${this.tabsId}-tab-panel-${tabId}`);
            tabId++;
        });
    }
}

let tabsId = 0;
